import React, {
  forwardRef,
  MutableRefObject,
  Ref,
  RefObject,
  VFC,
  useContext,
  useMemo,
} from 'react';

import BlockAsset from '/components/BlockAsset';
import BlockContent from '/components/BlockContent';
import BlockContentMedia from '/components/BlockContentMedia';
import BlockContentStat from '/components/BlockContentStat';
import BlockFullHeightMedia from '/components/BlockFullHeightMedia';
import BlockFullImage from '/components/BlockFullImage';
import BlockHero from '/components/BlockHero';
import BlockImageWall from '/components/BlockImageWall';
import BlockInfographic from '/components/BlockInfographic';
import BlockProductWallAnalyst from '/components/BlockProductWallAnalyst';
import BlockQuadrant from '/components/BlockQuadrant';
import BlockQuote from '/components/BlockQuote';
import BlockSectionHeader from '/components/BlockSectionHeader';
import BlockStats from '/components/BlockStats';
import BlockTable from '/components/BlockTable';
import BlockTwoColMedia from '/components/BlockTwoColMedia';
import BlockTwoImage from '/components/BlockTwoImage';
import BlockTwoThirdTable from '/components/BlockTwoThirdTable';
import BlockVideo from '/components/BlockVideo';
import {
  ArticleBlockJson,
  BlockProps,
  BlockContentJson,
  PresentOptionsJson,
  ProcessedBlock,
  NullableHTMLDivElement,
  PresentOption,
  SendSnackbarEventFn,
  BlockProjectData,
} from '/types';
import { BlockBackgroundContext, PresentModeContext } from '/utils/contexts';
import {
  fixBackgroundType,
  fixBlockWallOfText,
  fixMissingType,
} from '/utils/dataFixes';
import { isDarkBackground } from '/utils/functions';
import usePresentControls, { isBlockAnimated } from '/utils/usePresentControls';

/* eslint-disable react/display-name */
export const blockMap: Record<string, VFC<BlockProps>> = {
  'block-asset': (props) => <BlockAsset {...props} />,
  'block-content': (props) => <BlockContent {...props} />,
  'block-content-2-cols': (props) => <BlockContent {...props} />,
  'block-content-3-cols': (props) => <BlockContent {...props} offset={1} />,
  'block-content-3-cols-numbered': (props) => (
    <BlockContent {...props} offset={1} />
  ),
  'block-content-media': (props) => <BlockContentMedia {...props} />,
  'block-content-stat': (props) => <BlockContentStat {...props} />,
  'block-content-quote': (props) => <BlockQuote {...props} />,
  'block-full-image': (props) => <BlockFullImage {...props} offset={1} />,
  'block-full-height-media-half': (props) => (
    <BlockFullHeightMedia {...props} />
  ),
  'block-full-height-media-two-third': (props) => (
    <BlockFullHeightMedia {...props} />
  ),
  'block-hero': (props) => <BlockHero {...props} />,
  'block-image-wall-x4': (props) => <BlockImageWall {...props} />,
  'block-image-wall-x7': (props) => <BlockImageWall {...props} />,
  'block-infographic': (props) => <BlockInfographic {...props} offset={1} />,
  'block-product-wall-analyst': (props) => (
    <BlockProductWallAnalyst {...props} />
  ),
  'block-quadrant': (props) => <BlockQuadrant {...props} />,
  'block-quote': (props) => <BlockQuote {...props} />,
  'block-section-header': (props) => <BlockSectionHeader {...props} />,
  'block-sub-section-header': (props) => (
    <BlockSectionHeader offset={1} sub {...props} />
  ),
  'block-stats': (props) => <BlockStats {...props} />,
  'block-two-col-media': (props) => <BlockTwoColMedia {...props} offset={1} />,
  'block-two-image': (props) => <BlockTwoImage {...props} offset={1} />,
  'block-two-third-table': (props) => <BlockTwoThirdTable {...props} />,
  'block-table': (props) => <BlockTable {...props} />,
  'block-video': (props) => <BlockVideo {...props} />,
  'xfer-block-big-image-no-title': (props) => <BlockInfographic {...props} />,
  'xfer-block-big-image-title': (props) => <BlockInfographic {...props} />,
  'xfer-block-svg': (props) => <BlockInfographic {...props} />,
  'xfer-block-wall-of-text': (props) => <BlockContent {...props} />,
};
/* eslint-enable react/display-name */

const getPresentOption = (
  presentOptions?: PresentOptionsJson,
): PresentOption => {
  if (presentOptions && presentOptions.full_text) {
    return PresentOption.Full;
  }

  if (presentOptions && presentOptions.selected_text) {
    return PresentOption.Selected;
  }

  return PresentOption.Default;
};

type BlockWrapperProps = {
  index?: number;
  onScreenBlock: MutableRefObject<string | undefined>;
  blockProps: ArticleBlockJson;
  ref: Ref<NullableHTMLDivElement[]>;
  numBlocks?: number;
  sendSnackbarEvent?: SendSnackbarEventFn;
  blocksProjectData?: BlockProjectData[];
};

const applyDataFixes = (blockProps: ArticleBlockJson): ProcessedBlock => {
  // TODO: parse the json before we send it to CPA
  const block: ProcessedBlock = {
    ...blockProps,
    json: JSON.parse(blockProps.json) as BlockContentJson,
  };

  fixBlockWallOfText(block);
  fixBackgroundType(block);
  fixMissingType(block);

  return block;
};

const BlockWrapper = forwardRef<NullableHTMLDivElement[], BlockWrapperProps>(
  (
    {
      index = 0,
      onScreenBlock,
      blockProps,
      numBlocks = 0,
      sendSnackbarEvent,
      blocksProjectData,
    },
    ref,
  ) => {
    const presentMode = useContext(PresentModeContext);
    const block = useMemo(() => applyDataFixes(blockProps), [blockProps]);

    const { animationEnabled, chartAnimationEnabled } = isBlockAnimated(
      block,
      presentMode,
    );
    const {
      PresentControls,
      animationStep,
      setAnimationComplete,
    } = usePresentControls({
      ref: ref as RefObject<NullableHTMLDivElement[]>,
      index,
      numBlocks,
      animationEnabled,
      chartAnimationEnabled,
      onScreenBlock,
    });

    const BlockComponent = blockMap[block.type];

    return (
      <BlockBackgroundContext.Provider
        value={{ darkBg: isDarkBackground(block.background_type) }}
      >
        <PresentControls>
          <BlockComponent
            blockId={block.id}
            blockType={block.type}
            blockContent={block.json}
            blockBackgroundType={block.background_type}
            blockBackgroundImage={block?.background_id}
            blockBackgroundOpacity={block['background_opacity']}
            blockIndex={index}
            blockLinked={false}
            key={block.id}
            presentOption={getPresentOption(block?.presentation_options)}
            animationStep={animationStep}
            animationEnabled={animationEnabled}
            setAnimationComplete={setAnimationComplete}
            sendSnackbarEvent={sendSnackbarEvent}
            blocksProjectData={blocksProjectData}
          />
        </PresentControls>
      </BlockBackgroundContext.Provider>
    );
  },
);

BlockWrapper.displayName = 'BlockWrapper';
export default BlockWrapper;
